import ChartWidgetRenderService from '@/modules/ta/legacy/widget/services/types/ChartWidgetRenderService';
import { isSmallPercentageInSeries } from '@/modules/core/legacy/charts/adapters.helper';
import { dataContext } from '@/modules/core/legacy/charts/events.helper';
import { PieChartDrawOption } from '@/modules/ta/legacy/widget/builder.constants';
import SliceWidgetConfigData from '@/modules/ta/legacy/widget/models/types/SliceWidgetConfigData';
import { isUndefined } from 'lodash';

export default class SliceChartWidgetRenderService extends ChartWidgetRenderService {
  /**
   * @var {boolean}
   */
  isComparison;

  /**
   * @var {Object}
   */
  periodData;

  /**
   * @param index
   * @param metric
   * @param rawValue
   * @param rawMetric
   * @returns {SliceWidgetConfigData}
   */
  getChartDefaultDatum(index, metric, rawValue, rawMetric) {
    let clientCurrency = null;
    if (
      !isUndefined(this.periodData) &&
      this.periodData.length &&
      !isUndefined(this.periodData[0].client_currency)
    ) {
      clientCurrency = this.periodData[0].client_currency;
    }
    return new SliceWidgetConfigData({ index, metric, rawValue, rawMetric, clientCurrency });
  }

  /**
   * @param datum
   * @returns {String}
   */
  getDataMetric(datum) {
    return this._isCurrentGroupedColumnDate()
      ? this._getVisibleDate(datum)
      : datum[this.getCurrentGroupByField()];
  }

  /**
   * @param datum
   * @param total
   * @returns {number}
   */
  getDatumPercent(datum, total) {
    return Number.parseFloat((datum.rawValue / total) * 100).toFixed(2);
  }

  /**
   * @param chartData
   * @param total
   * @returns {Array<SliceWidgetConfigData>}
   */
  getPercentUpdatedData(chartData, total) {
    const otherPercent = this.getDrawOptions().other_percent;
    return chartData.map((datum) => {
      datum.percent = this.getDatumPercent(datum, total);
      if (otherPercent) {
        datum.isOther = datum.percent <= otherPercent;
      }
      return datum;
    });
  }

  /**
   * @param chartData
   * @returns {Array<SliceWidgetConfigData>}
   */
  getNormalizedChartData(chartData) {
    return chartData.map((chartDatum) => {
      chartDatum.value = 1;
      return chartDatum;
    });
  }

  /**
   * @param chartData
   * @returns {Array<SliceWidgetConfigData>}
   */
  getUnNormalizedChartData(chartData) {
    return chartData.map((chartDatum) => {
      chartDatum.value = chartDatum.rawValue;
      return chartDatum;
    });
  }

  /**
   * @param datum
   * @param metricSuffix
   * @returns {string}
   */
  getDatumMetricLabel(datum, metricSuffix) {
    return metricSuffix
      ? // eslint-disable-next-line tap/no-raw-text-js
        `${this.getDataMetric(datum) ?? 'Unspecified'} ${metricSuffix}`
      : this.getDataMetric(datum);
  }

  /**
   * @param field
   * @param metricSuffix
   * @returns {Array<SliceWidgetConfigData>}
   */
  getChartDataFromPeriodData(field, metricSuffix) {
    return this.periodData.map((datum, index) =>
      this.getChartDefaultDatum(
        index,
        this.getDatumMetricLabel(datum, metricSuffix),
        datum[field],
        this.getDataMetric(datum)
      )
    );
  }

  /**
   * @param chartData
   * @param total
   * @returns {SliceWidgetConfigData}
   */
  getOtherPercentDatum(chartData, total) {
    const newDatum = this.getChartDefaultDatum(
      chartData.length,
      PieChartDrawOption.OTHERS_METRIC_LABEL,
      0
    );
    chartData.forEach((datum) => {
      if (datum.isOther) {
        newDatum.rawValue += datum.rawValue;
        newDatum.subs = newDatum.subs.concat(datum.subs);
      }
    });
    newDatum.isOther = newDatum.rawValue === 0;
    newDatum.percent = this.getDatumPercent(newDatum, total);
    return newDatum;
  }

  /**
   * @param chartData
   * @param total
   * @returns {Array<SliceWidgetConfigData>}
   */
  getDrawOptionAppliedChartData(chartData, total) {
    const { other_percent, is_normalized } = this.getDrawOptions();
    let newChartData = this.getPercentUpdatedData(chartData, total);
    if (other_percent > 0) {
      newChartData.push(this.getOtherPercentDatum(newChartData, total));
    }
    newChartData = is_normalized
      ? this.getNormalizedChartData(newChartData)
      : this.getUnNormalizedChartData(newChartData);
    return newChartData.filter((datum) => !datum.isOther);
  }

  /**
   * @param field
   * @param total
   * @param metricSuffix
   * @returns {Array<SliceWidgetConfigData>}
   */
  getChartDataForSingleMetric(field, total, metricSuffix) {
    const chartData = this.getChartDataFromPeriodData(field, metricSuffix);
    return this.getDrawOptionAppliedChartData(chartData, total);
  }

  /**
   * @param index
   * @param column
   * @returns {Array<string|SliceWidgetConfigData>}
   */
  getChartDefaultDatumEntry(index, column) {
    return [column.field, this.getChartDefaultDatum(index, column.label, 0)];
  }

  /**
   * @returns {{}}
   */
  getChartDataFromNonGroupedColumns() {
    const defaultDatumEntries = this.nonGroupedColumns.map((column, index) =>
      this.getChartDefaultDatumEntry(index, column)
    );
    return Object.fromEntries(defaultDatumEntries);
  }

  /**
   * @param chartDataByField
   * @param field
   * @param fieldIndex
   * @param total
   * @param formulaField
   */
  updateChartDataByField(chartDataByField, field, fieldIndex, total, formulaField = []) {
    const subs = this.getChartDataForSingleMetric(field, total, chartDataByField[field].metric);
    chartDataByField[field].index = fieldIndex;
    chartDataByField[field].subs = subs;

    const totalDataStatus = Boolean(this._totalData?.[field]);
    subs.forEach((sub) => {
      if (!sub.isOther) {
        if (formulaField?.includes(field) && totalDataStatus) {
          const totalValue = Number(this._totalData[field]);
          chartDataByField[field].rawValue = totalValue;
          chartDataByField[field].value = totalValue;
        } else {
          chartDataByField[field].rawValue += sub.rawValue;
        }
      }
    });
  }

  /**
   * @param total
   * @returns {Array<SliceWidgetConfigData>}
   */
  getChartDataForMultipleMetrics(total) {
    const chartDataByField = this.getChartDataFromNonGroupedColumns();
    const formulaField = this._widget ? this.fetchCalculationField(this._widget) : [];
    Object.keys(chartDataByField).forEach((field, index) => {
      this.updateChartDataByField(chartDataByField, field, index, total, formulaField);
    });
    return this.getDrawOptionAppliedChartData(Object.values(chartDataByField), total);
  }

  /**
   * @returns {Number}
   */
  getChartDataTotal() {
    return this.periodData.reduce(
      (dataTotal, datum) =>
        dataTotal +
        this.nonGroupedColumns.reduce(
          (metricTotal, column) => metricTotal + Number(datum[column.field]),
          0
        ),
      0
    );
  }

  /**
   * @returns {Array<SliceWidgetConfigData>}
   */
  getChartData() {
    const total = this.getChartDataTotal();
    if (this.nonGroupedColumns.length > 1) {
      return this.getChartDataForMultipleMetrics(total);
    }
    return this.getChartDataForSingleMetric(this.nonGroupedColumns[0]?.field, total);
  }

  /**
   * @returns {string}
   */
  getTooltipTextTemplate() {
    return '{metric}: {rawValue} ({percent}%)';
  }

  /**
   * @returns {string}
   * @private
   */
  getSeriesText() {
    let text = '';
    const { show_labels, show_label_names, show_label_values, show_label_percent } =
      this.getDrawOptions();
    if (!show_labels) {
      return text;
    }
    if (show_label_percent) {
      text += '{percent}%';
    }
    if (show_label_values) {
      text = text !== '' ? `{rawValue} (${text})` : '{rawValue}';
    }
    if (show_label_names) {
      text = text !== '' ? `{metric}: ${text}` : '{metric}';
    }
    return text;
  }

  /**
   * @returns {{any}}
   */
  getSeries() {
    const { label_percent, has_tooltip } = this.getDrawOptions();
    const labelText = this.getSeriesText();
    return {
      value: 'value',
      category: 'metric',
      labelText,
      showTicks: !!labelText,
      showLabels: !!labelText,
      alignLabels: !!labelText,
      seriesType: this.getSeriesType(),
      fillOpacity: this.isComparison ? 0.6 : 1,
      colors: this.getFullChartPalette(),
      tooltipDisabled: !has_tooltip,
      tooltipText: this.getTooltipTextTemplate(),
      isLabelHidden: (hidden, target) => isSmallPercentageInSeries(target, label_percent),
    };
  }

  /**
   * @returns {{any}}
   */
  getChartSeries() {
    return [this.getSeries()];
  }

  /**
   * @returns {{valueLabelTemplate: string}}
   */
  getChartLegend() {
    return {
      ...super.getChartLegend(),
      valueLabelTemplate: '{rawValue}',
    };
  }

  /**
   * @param isComparison
   */
  setIsComparison(isComparison) {
    this.isComparison = isComparison;
  }

  setPeriodData() {
    this.periodData = this.isComparison ? this.priorPeriodData : this.currentPeriodData;
  }

  /**
   * @param metadata
   */
  setup(metadata) {
    super.setup();
    this.setIsComparison(metadata.isComparison);
    this.setPeriodData();
  }

  /**
   * @returns {null|Array}
   */
  getChartTitles() {
    return this.canUndoDrillDown() ? [this.getUndoDrillDownLabel()] : null;
  }

  /**
   * @returns {{}}
   */
  getChartConfigProperties() {
    return {
      ...super.getChartConfigProperties(),
      labels: this.hasComparison() ? [this.getPeriodLabel()] : null,
      cursorOverStyle: this.getCursorOverStyle(),
      canDrillDown: this.canDrillDown(),
      isInlineDrillDown: this.isInlineDrillDown(),
    };
  }

  /**
   * @returns {boolean}
   */
  isInlineDrillDown() {
    return this.isMultiGrouped() && this.getGroupedColumns().length > 1;
  }

  /**
   * @param isDrillDownNotApplied
   * @returns {boolean}
   */
  canDrillDown(isDrillDownNotApplied = true) {
    return (
      super.canDrillDown() &&
      (this.getGroupByIndexOnDrillDown() !== this.currentGroupByIndex || isDrillDownNotApplied)
    );
  }

  /**
   * @param event
   * @returns {DrillDownConfig}
   */
  drillDown(event) {
    const categoryIndex = dataContext(event)?.index;
    this.setCurrentGroupByIndex(this.getGroupByIndexOnDrillDown());
    const drillDownConfig = this.getDrillDownConfig(categoryIndex, this.periodData);
    // Below we push and return last element in drillDownConfigStack
    this.drillDownConfigStack.push(drillDownConfig);
    return drillDownConfig;
  }
}
